import { computed, defineAsyncComponent } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { GlBreakpointInstance, breakpoints } from '@gitlab/ui/src/utils'; // eslint-disable-line no-restricted-syntax -- GlBreakpointInstance is used intentionally here. In this case we must obtain viewport breakpoints
import { PortalTarget } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/portal_vue_vue3.js?vue3';
import { Mousetrap } from '~/lib/mousetrap';
import { TAB_KEY_CODE } from '~/lib/utils/keycodes';
import { keysFor, TOGGLE_SUPER_SIDEBAR } from '~/behaviors/shortcuts/keybindings';
import { s__ } from '~/locale';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import AccessorUtilities from '~/lib/utils/accessor';
import { JS_TOGGLE_EXPAND_CLASS, PANEL_TYPES, PANELS_WITH_HIDEABLE_UNPINNED_ITEMS, SETTINGS_DISCLOSURE_PORTAL_NAME, SIDEBAR_NAV_MODE_COMPACT, SIDEBAR_NAV_MODE_FULL, SIDEBAR_NAV_MODES, SIDEBAR_NAV_MODE_STORAGE_KEY } from '../constants';
import { sidebarState } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/state.js?vue3';
import { isCollapsed, toggleSuperSidebarCollapsed, toggleSuperSidebarIconOnly } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/super_sidebar_collapsed_state_manager.js?vue3';
import { trackContextAccess } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/utils.js?vue3';
import IconOnlyToggle from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/icon_only_toggle.vue?vue3';
import ManageOrganizationButton from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/manage_organization_button.vue?vue3';
import HelpCenter from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/help_center.vue?vue3';
import SidebarMenu from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/sidebar_menu.vue?vue3';
import SidebarPreferences from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/sidebar_preferences.vue?vue3';
import ScrollScrim from './scroll_scrim.vue';
export default {
    name: 'SuperSidebar',
    components: {
        IconOnlyToggle,
        ManageOrganizationButton,
        HelpCenter,
        SidebarMenu,
        SidebarPreferences,
        ScrollScrim,
        PortalTarget,
        TrialWidget: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/contextual_sidebar/components/trial_widget.vue?vue3'))
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    settingsDisclosurePortalName: SETTINGS_DISCLOSURE_PORTAL_NAME,
    i18n: {
        primaryNavigation: s__('Navigation|Primary navigation')
    },
    inject: [
        'showTrialWidget'
    ],
    provide () {
        return {
            isIconOnly: computed(()=>this.isIconOnly),
            panelType: this.sidebarData.panel_type
        };
    },
    props: {
        sidebarData: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            sidebarState,
            isMouseover: false,
            isAnimatable: false,
            wasToggledManually: false,
            // User's chosen navigation mode (persisted), one of SIDEBAR_NAV_MODE_*.
            navMode: this.readNavMode()
        };
    },
    computed: {
        menuItems () {
            return this.sidebarData.current_menu_items || [];
        },
        sidebarClasses () {
            return {
                'super-sidebar-is-icon-only': this.isIconOnly,
                'super-sidebar-is-mobile': this.sidebarState.isMobile,
                'super-sidebar-animatable': this.isAnimatable,
                'super-sidebar-toggled-manually': this.wasToggledManually
            };
        },
        canIconOnly () {
            return !this.sidebarState.isMobile;
        },
        isIconOnly () {
            return this.canIconOnly && this.sidebarState.isIconOnly;
        },
        manageOrganizationPath () {
            return this.sidebarData.manage_organization_link;
        },
        isInOrganizationAdminArea () {
            return this.sidebarData.panel_type === PANEL_TYPES.ORGANIZATION_ADMIN;
        },
        panelType () {
            return this.sidebarData.panel_type;
        },
        // Mirror of SidebarMenu#showUnpinnedItems: the control only makes sense when
        // the flag hides unpinned items for a pinnable, non-organization panel.
        canToggleOldCategories () {
            return this.glFeatures.hideUnpinnedSidebarItems && PANELS_WITH_HIDEABLE_UNPINNED_ITEMS.includes(this.panelType);
        },
        showOldCategories () {
            return this.navMode === SIDEBAR_NAV_MODE_FULL;
        },
        // Settings portals into the context header when it renders, else the footer.
        showHeaderSettingsDisclosure () {
            return this.glFeatures.hideUnpinnedSidebarItems && Boolean(this.sidebarData.current_context_header) && !this.isIconOnly;
        },
        showFooterSettingsDisclosure () {
            return this.glFeatures.hideUnpinnedSidebarItems && !this.showHeaderSettingsDisclosure;
        }
    },
    watch: {
        'sidebarState.isCollapsed': {
            handler (collapsed) {
                this.setupFocusTrapListener();
                if (!collapsed) {
                    this.$nextTick(()=>{
                        this.firstFocusableElement().focus();
                    });
                }
            }
        }
    },
    created () {
        const { is_logged_in: isLoggedIn, current_context: currentContext, username } = this.sidebarData;
        if (isLoggedIn && currentContext.namespace) {
            trackContextAccess(username, currentContext);
        }
    },
    mounted () {
        this.setupFocusTrapListener();
        Mousetrap.bind(keysFor(TOGGLE_SUPER_SIDEBAR), this.toggleSidebar);
        this.$nextTick(()=>{
            this.isAnimatable = true;
        });
    },
    beforeDestroy () {
        document.removeEventListener('keydown', this.focusTrap);
        Mousetrap.unbind(keysFor(TOGGLE_SUPER_SIDEBAR));
    },
    methods: {
        toggleSidebar () {
            if (this.canIconOnly) {
                this.wasToggledManually = true;
                toggleSuperSidebarIconOnly();
            } else {
                // on mobile
                toggleSuperSidebarCollapsed(!isCollapsed());
            }
        },
        isOverlapping () {
            return GlBreakpointInstance.windowWidth() < breakpoints.xl;
        },
        setupFocusTrapListener () {
            /**
       * Only trap focus when sidebar displays over page content to avoid
       * focus moving to page content and being obscured by the sidebar
       */ if (this.isOverlapping() && !this.sidebarState.isCollapsed) {
                document.addEventListener('keydown', this.focusTrap);
            } else {
                document.removeEventListener('keydown', this.focusTrap);
            }
        },
        collapseSidebar () {
            toggleSuperSidebarCollapsed(true);
        },
        handleEscKey () {
            if (this.isOverlapping()) {
                this.collapseSidebar();
                document.querySelector(`.${JS_TOGGLE_EXPAND_CLASS}`)?.focus();
            }
        },
        firstFocusableElement () {
            // Falls back to the always-present Help Center toggle, so focus lands
            // somewhere even when the menu has no links (e.g. unpinned items hidden).
            return this.$refs.sidebarMenu.$el.querySelector('a') || this.lastFocusableElement();
        },
        lastFocusableElement () {
            return this.$refs.helpCenter.$el.querySelector('button');
        },
        focusTrap (event) {
            const { keyCode, shiftKey } = event;
            const firstFocusableElement = this.firstFocusableElement();
            const lastFocusableElement = this.lastFocusableElement();
            if (keyCode !== TAB_KEY_CODE) return;
            if (shiftKey) {
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    event.preventDefault();
                }
            } else if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                event.preventDefault();
            }
        },
        handleTransitionEnd () {
            this.wasToggledManually = false;
        },
        readNavMode () {
            if (!AccessorUtilities.canUseLocalStorage()) return SIDEBAR_NAV_MODE_COMPACT;
            const stored = localStorage.getItem(SIDEBAR_NAV_MODE_STORAGE_KEY);
            // Retired modes (pinned_only, grouped_pins) fall back to compact here.
            return SIDEBAR_NAV_MODES.includes(stored) ? stored : SIDEBAR_NAV_MODE_COMPACT;
        },
        setNavMode (mode) {
            this.navMode = mode;
            if (AccessorUtilities.canUseLocalStorage()) {
                localStorage.setItem(SIDEBAR_NAV_MODE_STORAGE_KEY, mode);
            }
        }
    }
};
